import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Box,
  IconButton,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { CPEFormDialog } from './CPEFormDialog';
import { ZoneFormDialog } from './ZoneFormDialog';
import { Zone } from '../types/zone';

interface TabbedCreateModalProps {
  open: boolean;
  onClose: () => void;
  initialLatitude?: number;
  initialLongitude?: number;
  coords?: [number, number] | null;
  selectedTab?: 'pp' | 'zone';
  onTabChange?: (tab: 'pp' | 'zone') => void;
  onCreated?: () => void;
  defaultZone?: Zone | null;
}

const TabbedCreateModal: React.FC<TabbedCreateModalProps> = ({
  open,
  onClose,
  initialLatitude,
  initialLongitude,
  coords,
  selectedTab,
  onTabChange,
  onCreated,
  defaultZone,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'pp' | 'zone'>(selectedTab || 'pp');

  // Extract coordinates from coords prop if provided
  const latitude = coords ? coords[0] : initialLatitude;
  const longitude = coords ? coords[1] : initialLongitude;

  // Update activeTab when selectedTab prop changes
  React.useEffect(() => {
    if (selectedTab) {
      setActiveTab(selectedTab);
    }
  }, [selectedTab]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: 'pp' | 'zone') => {
    setActiveTab(newValue);
    onTabChange?.(newValue);
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh' },
      }}
    >
      <DialogTitle sx={{ pb: 0 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{t('cpe.createNew')}</Typography>
            <IconButton onClick={handleClose} edge="end">
              <CloseIcon />
            </IconButton>
          </Box>
          <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab label={t('cpe.createNew', 'Create PP')} value="pp" />
            <Tab label={t('zones.createNew', 'Create Zone')} value="zone" />
          </Tabs>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2 }}>
        {activeTab === 'pp' && (
          <CPEFormDialog
            open={true}
            onClose={handleClose}
            initialLatitude={latitude?.toFixed(6)}
            initialLongitude={longitude?.toFixed(6)}
            embedded={true}
            onCreated={onCreated}
            defaultZone={defaultZone}
          />
        )}
        {activeTab === 'zone' && (
          <ZoneFormDialog
            open={true}
            onClose={handleClose}
            initialLatitude={latitude?.toFixed(6)}
            initialLongitude={longitude?.toFixed(6)}
            embedded={true}
            onCreated={onCreated}
          />
        )}
      </DialogContent>

      <DialogActions>
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
          {/* Actions are handled within the individual dialogs */}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default TabbedCreateModal;
